import {
  ACTION_STATUS_LABELS,
  AGENDA_DETAIL_MAX,
  AGENDA_ITEM_KIND_LABELS,
  AGENDA_TITLE_MAX,
  DECISION_TEXT_MAX,
  JUSTIFICATION_MAX,
  MEETING_LEVEL_LABELS,
  MEETING_STATUS_LABELS,
  TRAFFIC_LIGHTS,
  TRAFFIC_LIGHT_LABELS,
  TREND_LABELS,
  agendaItemInputSchema,
  agendaOptionsSchema,
  agendaSchema,
  conductInputSchema,
  decisionInputSchema,
  decisionSchema,
  decisionUpdateSchema,
  formatDate,
  formatDateTime,
  formatDecimal,
  formatMonth,
  objectiveAnalysesSchema,
  type ActionStatusName,
  type AgendaItem,
  type AgendaOptions,
  type AgendaSnapshot,
  type Decision,
  type MeetingStatusName,
  type TrafficLight,
} from '@fluxus/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ClipboardList, ListPlus, RefreshCw, Trash2 } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan } from '@/lib/auth/session';
import { AuditLink } from '@/features/audit/AuditLink';
import { cn } from '@/lib/cn';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';
import {
  ApprovalSummary,
  ApproverPicker,
  SwapApproversDialog,
  approvalStarted,
} from '@/features/meetings/Approval';

const textareaClass =
  'w-full resize-y rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm shadow-sm focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 aria-invalid:border-status-red-solid';

const isLight = (v: string | null): v is TrafficLight =>
  v !== null && (TRAFFIC_LIGHTS as readonly string[]).includes(v);
const isActionStatus = (v: string): v is ActionStatusName => v in ACTION_STATUS_LABELS;

/** Valor com a unidade do KPI ("R$", "%" ou o nome da unidade). */
function withUnit(value: number | null, unit: string | null) {
  if (value === null) return '—';
  const n = formatDecimal(value, 2);
  if (!unit) return n;
  if (unit === 'R$') return `R$ ${n}`;
  if (unit === '%') return `${n}%`;
  return `${n} ${unit}`;
}

/** Link para a reunião em Gestão › Reuniões (abre o detalhe). */
export const meetingLink = (organizationId: string, projectId: string, meetingId: string) =>
  `/empresas/${organizationId}/gestao/reunioes?${new URLSearchParams({ projeto: projectId, reuniao: meetingId })}`;
export const actionLink = (organizationId: string, projectId: string, planId: string, actionId: string) =>
  `/empresas/${organizationId}/execucao/planos-acao?${new URLSearchParams({ projeto: projectId, plano: planId, acao: actionId })}`;

/** Retrato do assunto: resultado, meta, performance, semáforo e tendência de um mês. */
export function Snapshot({ title, snap }: { title: string; snap: AgendaSnapshot }) {
  return (
    <div className="min-w-0 rounded-md bg-muted/60 px-3 py-2">
      <p className="text-xs font-semibold text-muted-foreground">
        {title}
        {snap.period ? ` · ${formatMonth(`${snap.period}-15T12:00:00Z`)}` : ''}
      </p>
      <dl className="mt-1 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-sm">
        {snap.result !== null || snap.target !== null ? (
          <>
            <dt className="text-muted-foreground">Resultado</dt>
            <dd className="tabular-nums">{withUnit(snap.result, snap.unit)}</dd>
            <dt className="text-muted-foreground">Meta</dt>
            <dd className="tabular-nums">{withUnit(snap.target, snap.unit)}</dd>
          </>
        ) : null}
        <dt className="text-muted-foreground">Performance</dt>
        <dd className="tabular-nums">
          {snap.performance === null ? '—' : `${formatDecimal(snap.performance, 1)}%`}
        </dd>
        <dt className="text-muted-foreground">Semáforo</dt>
        <dd>
          {isLight(snap.light) ? (
            <StatusBadge status={snap.light} label={TRAFFIC_LIGHT_LABELS[snap.light]} />
          ) : (
            '—'
          )}
        </dd>
        <dt className="text-muted-foreground">Tendência</dt>
        <dd>
          {snap.trend
            ? `${TREND_LABELS[snap.trend.direction]}${
                snap.trend.favorable === null ? '' : snap.trend.favorable ? ' (favorável)' : ' (desfavorável)'
              }`
            : '—'}
        </dd>
      </dl>
    </div>
  );
}

/** Situação da decisão: cumprida, pendente ou o status da ação criada. */
function DecisionStatus({ d }: { d: Decision }) {
  if (d.action) {
    const label = isActionStatus(d.action.status) ? ACTION_STATUS_LABELS[d.action.status] : d.action.status;
    return <StatusBadge status={d.pending ? 'yellow' : 'green'} label={`Ação: ${label}`} />;
  }
  return d.pending ? (
    <StatusBadge status="yellow" label="Pendente" />
  ) : (
    <StatusBadge status="green" label="Cumprida" />
  );
}

export function DecisionLine({
  d,
  organizationId,
  projectId,
  children,
}: {
  d: Decision;
  organizationId: string;
  projectId: string;
  children?: ReactNode;
}) {
  const can = useCan();
  return (
    <div className="flex flex-col gap-1">
      <p className="break-words">{d.text}</p>
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <DecisionStatus d={d} />
        <span>Responsável: {d.responsible?.name ?? 'não definido'}</span>
        <span>Prazo: {d.dueDate ? formatDate(d.dueDate) : 'sem prazo'}</span>
        {d.action && can('planos-acao', 'view') ? (
          <Link
            className="font-medium text-brand-strong underline underline-offset-2 dark:text-brand"
            to={actionLink(organizationId, projectId, d.action.planId, d.action.id)}
          >
            Abrir a ação
          </Link>
        ) : null}
        <AuditLink
          organizationId={organizationId}
          scope={{ entity: 'meeting_decision', record: d.id, name: d.text }}
          label={`Auditoria da decisão ${d.text}`}
          compact
        />
      </p>
      <ApprovalSummary d={d} />
      {children}
    </div>
  );
}

/**
 * Pauta e condução da análise crítica (T-121c, RF-38, RN-28; DEC-050): montar e atualizar a pauta, incluir e
 * retirar itens antes de iniciar; com a reunião em andamento, conduzir item a item (resultado, meta e tendência de
 * agora, justificativa, discutido, decisões e "criar ação"); depois, consulta. Decisão pendente de análise anterior
 * pode ser marcada como cumprida.
 */
export function CriticalAnalysisPanel({
  organizationId,
  projectId,
  meetingId,
  meetingStatus,
  readOnly,
}: {
  organizationId: string;
  projectId: string;
  meetingId: string;
  meetingStatus: MeetingStatusName;
  readOnly: boolean;
}) {
  const { client } = useAuth();
  const canDo = useCan();
  const queryClient = useQueryClient();
  const base = `/organizations/${organizationId}/projects/${projectId}/meetings/${meetingId}`;
  const key = ['meetings', projectId, 'agenda', meetingId];
  const agenda = useQuery({
    queryKey: [...key, meetingStatus],
    queryFn: ({ signal }) => client.request(`${base}/agenda`, { schema: agendaSchema, signal }),
  });
  const a = agenda.data;
  const options = useQuery({
    queryKey: [...key, 'options'],
    queryFn: ({ signal }) =>
      client.request(`${base}/agenda/options`, { schema: agendaOptionsSchema, signal }),
    enabled: Boolean(a && (a.canEdit || a.canConduct)),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<AgendaItem | null>(null);

  const generate = useMutation({
    meta: { handlesError: true },
    mutationFn: () => client.request(`${base}/agenda/generate`, { method: 'POST', schema: agendaSchema }),
    onSuccess: async (next) => {
      toast.success(a?.generatedAt ? 'Pauta atualizada' : 'Pauta montada', {
        description: next.items.length === 1 ? '1 item' : `${next.items.length} itens`,
      });
      await refresh();
    },
    onError: (err) => toast.error('Não foi possível montar a pauta', { description: errorMessage(err) }),
  });
  const remove = useMutation({
    meta: { handlesError: true },
    mutationFn: (item: AgendaItem) =>
      client.request(`${base}/agenda/items/${item.id}`, { method: 'DELETE', schema: agendaSchema }),
    onSuccess: async () => {
      setRemoving(null);
      toast.success('Item retirado da pauta');
      await refresh();
    },
    onError: (err) => toast.error('Não foi possível retirar o item', { description: errorMessage(err) }),
  });

  if (agenda.isPending) return <LoadingState rows={3} label="Carregando a pauta" />;
  if (agenda.isError) {
    return (
      <ErrorState error={agenda.error} onRetry={() => void agenda.refetch()} retrying={agenda.isFetching} />
    );
  }
  const data = agenda.data;
  const canEdit = data.canEdit && !readOnly;
  const canConduct = data.canConduct && !readOnly;

  return (
    <section aria-label="Pauta da análise crítica" className="flex flex-col gap-3 text-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-bold">Pauta da análise crítica</h3>
          <p className="text-xs text-muted-foreground">
            {data.generatedAt ? `Montada em ${formatDateTime(data.generatedAt)}` : 'Pauta ainda não montada'}
            {data.previous
              ? ` · mudanças desde "${data.previous.title}" (${formatDate(data.previous.startsAt)})`
              : ' · sem análise anterior do mesmo nível'}
          </p>
        </div>
        {canEdit ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" disabled={generate.isPending} onClick={() => setAdding(true)}>
              <ListPlus aria-hidden />
              Incluir item
            </Button>
            <Button size="sm" loading={generate.isPending} onClick={() => generate.mutate()}>
              <RefreshCw aria-hidden />
              {data.generatedAt ? 'Atualizar pauta' : 'Montar pauta'}
            </Button>
          </div>
        ) : null}
      </div>
      {meetingStatus === 'IN_PROGRESS' && !canConduct && !readOnly ? (
        <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
          Você acompanha esta análise: a condução é de quem tem "editar" em Análise Crítica na área da
          reunião.
        </p>
      ) : null}
      {data.items.length === 0 ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-8"
          icon={ClipboardList}
          title={data.generatedAt ? 'Nada na pauta' : 'Pauta ainda não montada'}
          description={
            data.generatedAt
              ? `Não há indicadores críticos, objetivos em risco, ações atrasadas, desvios, decisões pendentes nem mudanças no escopo desta reunião.${canEdit ? ' Inclua itens se precisar.' : ''}`
              : canEdit
                ? 'Monte a pauta: o sistema traz indicadores críticos, objetivos em risco, ações atrasadas, desvios, decisões pendentes e mudanças desde a última análise. Se a análise for iniciada sem pauta, ela é montada ao iniciar.'
                : 'Quem prepara a reunião monta a pauta; se ela for iniciada sem pauta, a pauta é montada ao iniciar.'
          }
        />
      ) : (
        <ol aria-label="Itens da pauta" className="flex flex-col gap-3">
          {data.items.map((item, i) => (
            <AgendaItemCard
              key={item.id}
              index={i + 1}
              item={item}
              base={base}
              organizationId={organizationId}
              projectId={projectId}
              meetingStatus={meetingStatus}
              canEdit={canEdit}
              canConduct={canConduct}
              canFulfill={!readOnly && canDo('analise-critica', 'edit')}
              canCreateAction={canDo('planos-acao', 'create')}
              options={options.data}
              optionsError={options.isError}
              onRemove={() => setRemoving(item)}
              onChanged={refresh}
            />
          ))}
        </ol>
      )}
      {data.hidden || data.dismissed ? (
        <p className="text-xs text-muted-foreground">
          {data.hidden
            ? `${data.hidden === 1 ? '1 item' : `${data.hidden} itens`} de áreas fora do seu escopo não aparecem. `
            : ''}
          {data.dismissed
            ? `${data.dismissed === 1 ? '1 item automático foi retirado' : `${data.dismissed} itens automáticos foram retirados`} e não volta ao atualizar.`
            : ''}
        </p>
      ) : null}
      <Dialog
        open={adding}
        onOpenChange={(open) => !open && setAdding(false)}
        title="Incluir item na pauta"
        className="w-[min(36rem,calc(100vw-2rem))]"
      >
        {!adding ? null : options.isPending ? (
          <LoadingState rows={2} label="Carregando indicadores, objetivos e ações" />
        ) : options.isError ? (
          <ErrorState
            error={options.error}
            onRetry={() => void options.refetch()}
            retrying={options.isFetching}
          />
        ) : (
          <AgendaItemForm
            base={base}
            options={options.data}
            onSaved={async () => {
              setAdding(false);
              toast.success('Item incluído na pauta');
              await refresh();
            }}
            onCancel={() => setAdding(false)}
          />
        )}
      </Dialog>
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title="Retirar o item da pauta?"
        description={
          removing?.automatic
            ? `"${removing.title}" sai da pauta e não volta ao atualizar.`
            : `"${removing?.title ?? ''}" sai da pauta.`
        }
        confirmLabel="Retirar"
        destructive
        busy={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing)}
      />
    </section>
  );
}

function AgendaItemCard({
  index,
  item,
  base,
  organizationId,
  projectId,
  meetingStatus,
  canEdit,
  canConduct,
  canFulfill,
  canCreateAction,
  options,
  optionsError,
  onRemove,
  onChanged,
}: {
  index: number;
  item: AgendaItem;
  base: string;
  organizationId: string;
  projectId: string;
  meetingStatus: MeetingStatusName;
  canEdit: boolean;
  canConduct: boolean;
  canFulfill: boolean;
  canCreateAction: boolean;
  options: AgendaOptions | undefined;
  optionsError: boolean;
  onRemove: () => void;
  onChanged: () => Promise<unknown>;
}) {
  const { client } = useAuth();
  const titleId = useId();
  const canSeeActions = useCan()('planos-acao', 'view');
  const pending = item.pendingDecision;
  const [swapping, setSwapping] = useState<Decision | null>(null);
  const fulfill = useMutation({
    meta: { handlesError: true },
    mutationFn: (d: Decision) =>
      client.request(
        `/organizations/${organizationId}/projects/${projectId}/meeting-decisions/${d.id}/done`,
        { method: 'POST', body: { done: !d.done }, schema: decisionSchema },
      ),
    onSuccess: async (d) => {
      toast.success(d.done ? 'Decisão marcada como cumprida' : 'Decisão reaberta');
      await onChanged();
    },
    onError: (err) => toast.error('Não foi possível mudar a decisão', { description: errorMessage(err) }),
  });

  return (
    <li className="rounded-lg border border-border bg-surface p-3">
      <article aria-labelledby={titleId} className="flex flex-col gap-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
          <div className="min-w-0 flex-1">
            <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="font-semibold tabular-nums">{index}.</span>
              <span className="font-semibold uppercase">{AGENDA_ITEM_KIND_LABELS[item.kind]}</span>
              {item.department ? <span>{item.department.name}</span> : null}
              {item.businessUnit ? <span>{item.businessUnit.name}</span> : null}
              {item.discussed ? <StatusBadge status="green" label="Discutido" /> : null}
            </p>
            <h4 id={titleId} className="mt-1 font-bold break-words">
              {item.title}
            </h4>
            {item.detail ? <p className="mt-0.5 break-words text-muted-foreground">{item.detail}</p> : null}
            {item.action && canSeeActions ? (
              <Link
                className="mt-1 inline-block text-xs font-medium text-brand-strong underline underline-offset-2 dark:text-brand"
                to={actionLink(organizationId, projectId, item.action.planId, item.action.id)}
              >
                Abrir a ação no plano
              </Link>
            ) : null}
          </div>
          {canEdit ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemove}
              aria-label={`Retirar ${item.title} da pauta`}
            >
              <Trash2 aria-hidden />
              Retirar
            </Button>
          ) : null}
        </div>

        {item.snapshot || item.current ? (
          <div className="grid gap-2 sm:grid-cols-2">
            {item.snapshot ? <Snapshot title="Na montagem da pauta" snap={item.snapshot} /> : null}
            {item.current ? <Snapshot title="Agora" snap={item.current} /> : null}
          </div>
        ) : null}

        {pending ? (
          <div className="rounded-md border border-border px-3 py-2">
            <p className="text-xs font-semibold text-muted-foreground">Decisão em acompanhamento</p>
            <DecisionLine d={pending} organizationId={organizationId} projectId={projectId}>
              {canFulfill && (!pending.action || pending.approval.status === 'PENDING') ? (
                <div className="flex flex-wrap gap-1">
                  {!pending.action ? (
                    <Button
                      variant="outline"
                      size="sm"
                      loading={fulfill.isPending}
                      onClick={() => fulfill.mutate(pending)}
                      aria-label={`${pending.done ? 'Reabrir' : 'Marcar como cumprida'} a decisão ${pending.text}`}
                    >
                      <CheckCircle2 aria-hidden />
                      {pending.done ? 'Reabrir a decisão' : 'Marcar como cumprida'}
                    </Button>
                  ) : null}
                  {pending.approval.status === 'PENDING' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSwapping(pending)}
                      aria-label={`Trocar os aprovadores da decisão ${pending.text}`}
                    >
                      Trocar aprovadores
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </DecisionLine>
          </div>
        ) : null}
        <SwapApproversDialog
          organizationId={organizationId}
          projectId={projectId}
          decision={swapping}
          onClose={() => setSwapping(null)}
          onSaved={async () => {
            setSwapping(null);
            toast.success('Aprovadores atualizados');
            await onChanged();
          }}
        />

        {canConduct ? (
          <ConductForm item={item} base={base} onChanged={onChanged} />
        ) : meetingStatus !== 'SCHEDULED' && (item.justification || item.discussed) ? (
          <div>
            <p className="text-xs font-semibold text-muted-foreground">
              {item.discussed
                ? `Discutido${item.discussedByName ? ` · marcado por ${item.discussedByName}` : ''}${item.discussedAt ? ` em ${formatDateTime(item.discussedAt)}` : ''}`
                : 'Ainda não discutido'}
            </p>
            {item.justification ? (
              <p className="mt-0.5 whitespace-pre-line break-words">{item.justification}</p>
            ) : null}
          </div>
        ) : null}

        {meetingStatus !== 'SCHEDULED' && (item.decisions.length || canConduct) ? (
          <Decisions
            item={item}
            base={base}
            organizationId={organizationId}
            projectId={projectId}
            canConduct={canConduct}
            canFulfill={canFulfill}
            canCreateAction={canCreateAction}
            options={options}
            optionsError={optionsError}
            onChanged={onChanged}
          />
        ) : null}
      </article>
    </li>
  );
}

/** Justificativa e discutido do item (reunião em andamento). */
function ConductForm({
  item,
  base,
  onChanged,
}: {
  item: AgendaItem;
  base: string;
  onChanged: () => Promise<unknown>;
}) {
  const { client } = useAuth();
  const id = useId();
  const [justification, setJustification] = useState(item.justification ?? '');
  const [discussed, setDiscussed] = useState(item.discussed);
  const [errors, setErrors] = useState<FieldErrors>({});
  useEffect(() => {
    setJustification(item.justification ?? '');
    setDiscussed(item.discussed);
  }, [item.justification, item.discussed]);
  const dirty = justification !== (item.justification ?? '') || discussed !== item.discussed;
  const save = useMutation({
    meta: { handlesError: true },
    mutationFn: (body: { justification: string | null; discussed: boolean }) =>
      client.request(`${base}/agenda/items/${item.id}/conduct`, {
        method: 'PUT',
        body,
        schema: agendaSchema,
      }),
    onSuccess: async () => {
      setErrors({});
      toast.success('Condução do item salva', { description: item.title });
      await onChanged();
    },
    onError: (err) => {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else toast.error('Não foi possível salvar a condução', { description: errorMessage(err) });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = conductInputSchema.safeParse({ justification, discussed });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    save.mutate({ justification: parsed.data.justification, discussed: parsed.data.discussed });
  };
  return (
    <form
      onSubmit={submit}
      noValidate
      className="flex flex-col gap-2"
      aria-label={`Condução de ${item.title}`}
    >
      <FormField id={id} label="Justificativa" error={errors.justification}>
        <textarea
          {...fieldA11y(id, errors.justification)}
          value={justification}
          rows={3}
          maxLength={JUSTIFICATION_MAX}
          onChange={(e) => setJustification(e.target.value)}
          className={textareaClass}
          placeholder="Por que o resultado está assim e o que muda daqui em diante"
        />
      </FormField>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="inline-flex min-h-11 items-center gap-2">
          <input
            type="checkbox"
            className="size-4 accent-(--brand)"
            checked={discussed}
            onChange={(e) => setDiscussed(e.target.checked)}
          />
          Item discutido
        </label>
        <Button type="submit" variant="outline" size="sm" loading={save.isPending} disabled={!dirty}>
          Salvar condução
        </Button>
      </div>
    </form>
  );
}

/** Decisões do item: lista e, na condução, registrar, alterar e excluir; cumprir as sem ação. */
function Decisions({
  item,
  base,
  organizationId,
  projectId,
  canConduct,
  canFulfill,
  canCreateAction,
  options,
  optionsError,
  onChanged,
}: {
  item: AgendaItem;
  base: string;
  organizationId: string;
  projectId: string;
  canConduct: boolean;
  canFulfill: boolean;
  canCreateAction: boolean;
  options: AgendaOptions | undefined;
  optionsError: boolean;
  onChanged: () => Promise<unknown>;
}) {
  const { client } = useAuth();
  const [editing, setEditing] = useState<Decision | null>(null);
  const [removing, setRemoving] = useState<Decision | null>(null);
  const [swapping, setSwapping] = useState<Decision | null>(null);
  const remove = useMutation({
    meta: { handlesError: true },
    mutationFn: (d: Decision) => client.request(`${base}/decisions/${d.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setRemoving(null);
      toast.success('Decisão excluída');
      await onChanged();
    },
    onError: (err) => toast.error('Não foi possível excluir a decisão', { description: errorMessage(err) }),
  });
  const fulfill = useMutation({
    meta: { handlesError: true },
    mutationFn: (d: Decision) =>
      client.request(
        `/organizations/${organizationId}/projects/${projectId}/meeting-decisions/${d.id}/done`,
        { method: 'POST', body: { done: !d.done }, schema: decisionSchema },
      ),
    onSuccess: async (d) => {
      toast.success(d.done ? 'Decisão marcada como cumprida' : 'Decisão reaberta');
      await onChanged();
    },
    onError: (err) => toast.error('Não foi possível mudar a decisão', { description: errorMessage(err) }),
  });

  return (
    <div className="flex flex-col gap-2 border-t border-border pt-3">
      <h5 className="text-xs font-semibold text-muted-foreground">Decisões</h5>
      {item.decisions.length ? (
        <ul aria-label={`Decisões de ${item.title}`} className="flex flex-col gap-2">
          {item.decisions.map((d) => (
            <li key={d.id} className="rounded-md bg-muted/50 px-3 py-2">
              {editing?.id === d.id && options ? (
                <DecisionForm
                  base={base}
                  item={item}
                  options={options}
                  canCreateAction={false}
                  initial={d}
                  onSaved={async () => {
                    setEditing(null);
                    toast.success('Decisão alterada');
                    await onChanged();
                  }}
                  onCancel={() => setEditing(null)}
                />
              ) : (
                <DecisionLine d={d} organizationId={organizationId} projectId={projectId}>
                  {(!d.action && (canConduct || canFulfill)) ||
                  (canFulfill && d.approval.status === 'PENDING') ? (
                    <div className="flex flex-wrap gap-1">
                      {canFulfill && !d.action ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={fulfill.isPending}
                          onClick={() => fulfill.mutate(d)}
                          aria-label={`${d.done ? 'Reabrir' : 'Marcar como cumprida'} a decisão ${d.text}`}
                        >
                          <CheckCircle2 aria-hidden />
                          {d.done ? 'Reabrir' : 'Marcar como cumprida'}
                        </Button>
                      ) : null}
                      {canFulfill && d.approval.status === 'PENDING' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSwapping(d)}
                          aria-label={`Trocar os aprovadores da decisão ${d.text}`}
                        >
                          Trocar aprovadores
                        </Button>
                      ) : null}
                      {/* Depois do primeiro voto, o conteúdo não muda e a decisão não sai (DEC-053). */}
                      {canConduct && !d.action && !approvalStarted(d) ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={!options}
                            onClick={() => setEditing(d)}
                            aria-label={`Alterar a decisão ${d.text}`}
                          >
                            Alterar
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setRemoving(d)}
                            aria-label={`Excluir a decisão ${d.text}`}
                          >
                            Excluir
                          </Button>
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </DecisionLine>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">Nenhuma decisão registrada neste item.</p>
      )}
      {canConduct ? (
        options ? (
          <DecisionForm
            key={item.decisions.length}
            base={base}
            item={item}
            options={options}
            canCreateAction={canCreateAction}
            initial={null}
            onSaved={async (d) => {
              toast.success(d.action ? 'Decisão registrada e ação criada' : 'Decisão registrada', {
                description: d.text,
              });
              await onChanged();
            }}
          />
        ) : optionsError ? (
          <p role="alert" className="text-xs text-status-red-fg">
            Não foi possível carregar os responsáveis: feche e abra a reunião para tentar de novo.
          </p>
        ) : (
          <LoadingState rows={1} label="Carregando os responsáveis" />
        )
      ) : null}
      <SwapApproversDialog
        organizationId={organizationId}
        projectId={projectId}
        decision={swapping}
        onClose={() => setSwapping(null)}
        onSaved={async () => {
          setSwapping(null);
          toast.success('Aprovadores atualizados');
          await onChanged();
        }}
      />
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title="Excluir a decisão?"
        description={`"${removing?.text ?? ''}" sai da análise. A trilha de auditoria guarda o registro.`}
        confirmLabel="Excluir"
        destructive
        busy={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing)}
      />
    </div>
  );
}

/** Registrar (ou alterar) a decisão: texto, responsável, prazo e, ao registrar, "criar ação". */
function DecisionForm({
  base,
  item,
  options,
  canCreateAction,
  initial,
  onSaved,
  onCancel,
}: {
  base: string;
  item: AgendaItem;
  options: AgendaOptions;
  canCreateAction: boolean;
  initial: Decision | null;
  onSaved: (d: Decision) => Promise<unknown>;
  onCancel?: () => void;
}) {
  const { client } = useAuth();
  const ids = { text: useId(), responsible: useId(), due: useId() };
  const [text, setText] = useState(initial?.text ?? '');
  const [responsibleId, setResponsibleId] = useState(initial?.responsible?.id ?? '');
  const [dueDate, setDueDate] = useState(initial?.dueDate ?? '');
  const [createAction, setCreateAction] = useState(false);
  const [approverIds, setApproverIds] = useState(initial?.approval.approvers.map((a) => a.id) ?? []);
  const [errors, setErrors] = useState<FieldErrors>({});
  const save = useMutation({
    meta: { handlesError: true },
    mutationFn: (body: Record<string, unknown>) =>
      initial
        ? client.request(`${base}/decisions/${initial.id}`, { method: 'PUT', body, schema: decisionSchema })
        : client.request(`${base}/decisions`, { method: 'POST', body, schema: decisionSchema }),
    onSuccess: async (d) => {
      setErrors({});
      await onSaved(d);
    },
    onError: (err) => {
      const fields = apiFieldErrors(err);
      setErrors(hasFieldErrors(fields) ? fields : { form: errorMessage(err) });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const raw = { text, responsibleId: responsibleId || null, dueDate: dueDate || null, approverIds };
    if (initial) {
      const parsed = decisionUpdateSchema.safeParse(raw);
      if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
      setErrors({});
      return save.mutate(parsed.data);
    }
    const parsed = decisionInputSchema.safeParse({ ...raw, agendaItemId: item.id, createAction });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    save.mutate(parsed.data);
  };
  return (
    <form
      onSubmit={submit}
      noValidate
      className="flex flex-col gap-2"
      aria-label={initial ? `Alterar a decisão ${initial.text}` : `Nova decisão sobre ${item.title}`}
    >
      {errors.form ? <FormAlert tone="error">{errors.form}</FormAlert> : null}
      <FormField id={ids.text} label={initial ? 'Decisão' : 'Nova decisão'} error={errors.text}>
        <textarea
          {...fieldA11y(ids.text, errors.text)}
          value={text}
          rows={2}
          maxLength={DECISION_TEXT_MAX}
          onChange={(e) => setText(e.target.value)}
          className={textareaClass}
          placeholder="O que foi decidido"
        />
      </FormField>
      <div className="grid gap-2 sm:grid-cols-2">
        <FormField id={ids.responsible} label="Responsável" error={errors.responsibleId}>
          <NativeSelect
            {...fieldA11y(ids.responsible, errors.responsibleId)}
            value={responsibleId}
            onChange={(e) => setResponsibleId(e.target.value)}
          >
            <option value="">Sem responsável</option>
            {options.people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.due} label="Prazo" error={errors.dueDate}>
          <Input
            {...fieldA11y(ids.due, errors.dueDate)}
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </FormField>
      </div>
      <ApproverPicker
        approvers={[
          ...(initial?.approval.approvers.filter((x) => !options.approvers.some((p) => p.id === x.id)) ?? []),
          ...options.approvers,
        ]}
        value={approverIds}
        onChange={setApproverIds}
        error={errors.approverIds}
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        {initial ? (
          <span />
        ) : canCreateAction ? (
          <label className="inline-flex min-h-11 items-center gap-2">
            <input
              type="checkbox"
              className="size-4 accent-(--brand)"
              checked={createAction}
              onChange={(e) => setCreateAction(e.target.checked)}
            />
            Criar ação no plano de ação (exige responsável e prazo)
          </label>
        ) : (
          <span className="text-xs text-muted-foreground">
            Seu perfil não cria planos de ação: a decisão é registrada sem a ação.
          </span>
        )}
        <div className="flex gap-2">
          {onCancel ? (
            <Button type="button" variant="ghost" size="sm" disabled={save.isPending} onClick={onCancel}>
              Cancelar
            </Button>
          ) : null}
          <Button type="submit" size="sm" loading={save.isPending}>
            {initial ? 'Salvar decisão' : createAction ? 'Registrar e criar ação' : 'Registrar decisão'}
          </Button>
        </div>
      </div>
    </form>
  );
}

type Subject = 'none' | 'kpi' | 'objective' | 'action';

/** Item avulso: assunto, detalhe e, se quiser, o indicador, o objetivo ou a ação do item. */
function AgendaItemForm({
  base,
  options,
  onSaved,
  onCancel,
}: {
  base: string;
  options: AgendaOptions;
  onSaved: () => Promise<unknown>;
  onCancel: () => void;
}) {
  const { client } = useAuth();
  const ids = { title: useId(), detail: useId(), subject: useId(), ref: useId() };
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [subject, setSubject] = useState<Subject>('none');
  const [refId, setRefId] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const save = useMutation({
    meta: { handlesError: true },
    mutationFn: (body: Record<string, unknown>) =>
      client.request(`${base}/agenda/items`, { method: 'POST', body, schema: agendaSchema }),
    onSuccess: () => onSaved(),
    onError: (err) => {
      const fields = apiFieldErrors(err);
      setErrors(hasFieldErrors(fields) ? fields : { form: errorMessage(err) });
    },
  });
  const refs =
    subject === 'kpi'
      ? options.kpis.map((k) => ({ id: k.id, name: `${k.code} · ${k.name}` }))
      : subject === 'objective'
        ? options.objectives
        : subject === 'action'
          ? options.actions.map((x) => ({ id: x.id, name: `${x.name} (${x.planName})` }))
          : [];
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (subject !== 'none' && !refId) return setErrors({ ref: 'Escolha o assunto vinculado ou "nenhum".' });
    const parsed = agendaItemInputSchema.safeParse({
      title,
      detail: detail || null,
      kpiId: subject === 'kpi' ? refId : null,
      objectiveId: subject === 'objective' ? refId : null,
      actionId: subject === 'action' ? refId : null,
    });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    save.mutate(parsed.data);
  };
  const refError = errors.ref ?? errors.kpiId ?? errors.objectiveId ?? errors.actionId;
  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4">
      {errors.form ? <FormAlert tone="error">{errors.form}</FormAlert> : null}
      <FormField id={ids.title} label="Assunto" error={errors.title}>
        <Input
          {...fieldA11y(ids.title, errors.title)}
          value={title}
          maxLength={AGENDA_TITLE_MAX}
          onChange={(e) => setTitle(e.target.value)}
        />
      </FormField>
      <FormField id={ids.detail} label="Detalhe (opcional)" error={errors.detail}>
        <textarea
          {...fieldA11y(ids.detail, errors.detail)}
          value={detail}
          rows={3}
          maxLength={AGENDA_DETAIL_MAX}
          onChange={(e) => setDetail(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField id={ids.subject} label="Vincular a">
          <NativeSelect
            id={ids.subject}
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value as Subject);
              setRefId('');
            }}
          >
            <option value="none">Nenhum assunto</option>
            <option value="kpi">Indicador</option>
            <option value="objective">Objetivo</option>
            <option value="action">Ação</option>
          </NativeSelect>
        </FormField>
        {subject !== 'none' ? (
          <FormField id={ids.ref} label="Qual" error={refError}>
            <NativeSelect
              {...fieldA11y(ids.ref, refError)}
              value={refId}
              onChange={(e) => setRefId(e.target.value)}
            >
              <option value="">{refs.length ? 'Escolha' : 'Nada disponível no seu escopo'}</option>
              {refs.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </NativeSelect>
          </FormField>
        ) : null}
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" disabled={save.isPending} onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" loading={save.isPending}>
          Incluir na pauta
        </Button>
      </div>
    </form>
  );
}

/**
 * Aba "Análises críticas" do objetivo (T-121c, RN-28): os itens discutidos nas análises críticas sobre o objetivo
 * (direto ou por um KPI dele), com a justificativa, o retrato e as decisões.
 */
export function ObjectiveAnalyses({
  organizationId,
  projectId,
  objectiveId,
}: {
  organizationId: string;
  projectId: string;
  objectiveId: string;
}) {
  const { client } = useAuth();
  const can = useCan();
  const list = useQuery({
    queryKey: ['objectives', projectId, 'critical-analyses', objectiveId],
    queryFn: ({ signal }) =>
      client.request(
        `/organizations/${organizationId}/projects/${projectId}/objectives/${objectiveId}/critical-analyses`,
        { schema: objectiveAnalysesSchema, signal },
      ),
  });
  if (list.isPending) return <LoadingState rows={3} label="Carregando as análises críticas" />;
  if (list.isError) {
    return <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />;
  }
  if (!list.data.items.length) {
    return (
      <EmptyState
        className="py-8"
        icon={ClipboardList}
        title="Nenhuma análise crítica discutiu este objetivo"
        description="Quando um item ligado a ele (ou a um indicador dele) for marcado como discutido numa análise crítica, ele aparece aqui com as decisões."
      />
    );
  }
  return (
    <ol aria-label="Análises críticas do objetivo" className="flex flex-col gap-3 text-sm">
      {list.data.items.map(({ meeting, item, decisions }) => (
        <li key={item.id} className="rounded-lg border border-border bg-surface p-3">
          <article aria-label={`${meeting.title}: ${item.title}`} className="flex flex-col gap-2">
            <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="font-semibold">{meeting.title}</span>
              <span>{formatDate(meeting.startsAt)}</span>
              <span>{MEETING_LEVEL_LABELS[meeting.level]}</span>
              <span>{MEETING_STATUS_LABELS[meeting.status]}</span>
              {can('reunioes-decisoes', 'view') ? (
                <Link
                  className="font-medium text-brand-strong underline underline-offset-2 dark:text-brand"
                  to={meetingLink(organizationId, projectId, meeting.id)}
                >
                  Abrir a reunião
                </Link>
              ) : null}
            </p>
            <h4 className="font-bold break-words">
              {AGENDA_ITEM_KIND_LABELS[item.kind]}: {item.title}
            </h4>
            {item.snapshot ? <Snapshot title="Na pauta" snap={item.snapshot} /> : null}
            {item.justification ? (
              <p className="whitespace-pre-line break-words">
                <span className="text-muted-foreground">Justificativa: </span>
                {item.justification}
              </p>
            ) : null}
            {decisions.length ? (
              <ul aria-label="Decisões" className={cn('flex flex-col gap-2 border-t border-border pt-2')}>
                {decisions.map((d) => (
                  <li key={d.id}>
                    <DecisionLine d={d} organizationId={organizationId} projectId={projectId} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground">Sem decisões neste item.</p>
            )}
          </article>
        </li>
      ))}
    </ol>
  );
}
